import { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload, FileText, Mic, X, CheckCircle } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useMutation } from 'react-query'
import { ingestAPI } from '../../lib/api'
import toast from 'react-hot-toast'

export function DocumentUpload() {
  const [files, setFiles] = useState<File[]>([])
  const [uploadType, setUploadType] = useState<'file' | 'text' | 'voice'>('file')
  const [textContent, setTextContent] = useState('')
  const [textTitle, setTextTitle] = useState('')
  const [uploadStatus, setUploadStatus] = useState<Record<string, string>>({})
  const [isPublic, setIsPublic] = useState(false)

  const uploadMutation = useMutation(
    async (params: { files: File[], title?: string }) => {
      const results = []
      for (const file of params.files) {
        setUploadStatus(prev => ({ ...prev, [file.name]: 'uploading' }))
        try {
          const result = await ingestAPI.uploadFiles([file], params.title, { is_public: isPublic })
          setUploadStatus(prev => ({ ...prev, [file.name]: 'success' }))
          results.push(result)
        } catch (error) {
          setUploadStatus(prev => ({ ...prev, [file.name]: 'error' }))
          throw error
        }
      }
      return results
    },
    {
      onSuccess: () => {
        toast.success('Dokumente erfolgreich hochgeladen')
        setTimeout(() => {
          setFiles([])
          setUploadStatus({})
        }, 2000)
      },
      onError: () => {
        toast.error('Fehler beim Hochladen')
      }
    }
  )

  const textUploadMutation = useMutation(
    () => ingestAPI.uploadText(textTitle, textContent, { is_public: isPublic }),
    {
      onSuccess: () => {
        toast.success('Text erfolgreich gespeichert')
        setTextTitle('')
        setTextContent('')
      },
      onError: () => {
        toast.error('Fehler beim Speichern')
      }
    }
  )

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles(prev => [...prev, ...acceptedFiles])
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/plain': ['.txt'],
      'text/markdown': ['.md'],
    }
  })

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleUpload = () => {
    if (files.length > 0) {
      uploadMutation.mutate({ files })
    }
  }

  const handleTextUpload = () => {
    if (textTitle.trim() && textContent.trim()) {
      textUploadMutation.mutate()
    }
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Upload Type Selector */}
      <div className="flex space-x-4 mb-8">
        {[
          { id: 'file', label: 'Dateien', icon: FileText },
          { id: 'text', label: 'Text', icon: FileText },
          { id: 'voice', label: 'Sprache', icon: Mic },
        ].map((type) => {
          const Icon = type.icon
          return (
            <button
              key={type.id}
              onClick={() => setUploadType(type.id as any)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-smooth ${
                uploadType === type.id
                  ? 'bg-primary-600 text-white'
                  : 'glass hover:bg-white/10'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{type.label}</span>
            </button>
          )
        })}
      </div>

      {/* Public Document Checkbox */}
      <div className="glass rounded-lg p-4 mb-8">
        <label className="flex items-center space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={isPublic}
            onChange={(e) => setIsPublic(e.target.checked)}
            className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-primary-600 focus:ring-primary-500 focus:ring-2"
          />
          <div>
            <span className="font-medium">Dokument öffentlich machen</span>
            <p className="text-sm text-gray-400 mt-1">
              Öffentliche Dokumente können von allen Nutzern eingesehen werden
            </p>
          </div>
        </label>
      </div>

      {/* File Upload */}
      {uploadType === 'file' && (
        <div className="space-y-6">
          <div
            {...getRootProps()}
            className={`glass rounded-xl p-12 text-center cursor-pointer transition-all ${
              isDragActive ? 'border-primary-500 bg-primary-500/10' : 'border-gray-700'
            } border-2 border-dashed`}
          >
            <input {...getInputProps()} />
            <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-lg mb-2">
              {isDragActive
                ? 'Dateien hier ablegen...'
                : 'Dateien hierher ziehen oder klicken zum Auswählen'}
            </p>
            <p className="text-sm text-gray-400">
              PDF, Word, Excel, Text-Dateien
            </p>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-medium">Ausgewählte Dateien:</h3>
              {files.map((file, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="glass rounded-lg p-4 flex items-center justify-between"
                >
                  <div className="flex items-center space-x-3">
                    <FileText className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="font-medium">{file.name}</p>
                      <p className="text-sm text-gray-400">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {uploadStatus[file.name] === 'uploading' && (
                      <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                    )}
                    {uploadStatus[file.name] === 'success' && (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                    {uploadStatus[file.name] === 'error' && (
                      <X className="w-5 h-5 text-red-500" />
                    )}
                    {!uploadStatus[file.name] && (
                      <button
                        onClick={() => removeFile(index)}
                        className="p-1 hover:bg-white/10 rounded transition-smooth"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
              
              <button
                onClick={handleUpload}
                disabled={uploadMutation.isLoading}
                className="w-full btn btn-primary"
              >
                {uploadMutation.isLoading ? 'Wird hochgeladen...' : 'Hochladen'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Text Upload */}
      {uploadType === 'text' && (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2">Titel</label>
            <input
              type="text"
              value={textTitle}
              onChange={(e) => setTextTitle(e.target.value)}
              placeholder="Dokument-Titel eingeben..."
              className="input w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Inhalt</label>
            <textarea
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder="Text hier einfügen..."
              rows={12}
              className="input w-full"
            />
          </div>

          <button
            onClick={handleTextUpload}
            disabled={textUploadMutation.isLoading || !textTitle.trim() || !textContent.trim()}
            className="w-full btn btn-primary"
          >
            {textUploadMutation.isLoading ? 'Wird gespeichert...' : 'Speichern'}
          </button>
        </div>
      )}

      {/* Voice Upload */}
      {uploadType === 'voice' && (
        <div className="text-center py-12">
          <Mic className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <p className="text-lg mb-4">Sprachaufnahme-Funktion</p>
          <p className="text-gray-400">
            Diese Funktion wird in Kürze verfügbar sein
          </p>
        </div>
      )}
    </div>
  )
}